import { z } from 'zod';

import { LeadAccessSchema, LockedPreviewSchema } from './Lead';

// The API returns empty fields as null; accept them but expose them as undefined
const nullish = <T extends z.ZodTypeAny>(schema: T) => schema.nullish().transform((value) => value ?? undefined);

export enum QrScanType {
    PROFILE = 'profile',
    COLLATERAL = 'collateral',
    PRODUCT = 'product',
}

export enum QrScanSource {
    QR_CODE = 'qrCode',
    WEB = 'web',
}

export const QrScanSourceOptions = [
    { value: QrScanSource.QR_CODE, label: 'QR code' },
    { value: QrScanSource.WEB, label: 'Website' },
];

export const QrScanTypeOptions = [
    { value: QrScanType.PROFILE, label: 'Profile' },
    { value: QrScanType.COLLATERAL, label: 'Collateral' },
    { value: QrScanType.PRODUCT, label: 'Product' },
];

export const QrScanSchema = z.object({
    id: nullish(z.string()),
    type: nullish(z.nativeEnum(QrScanType)),
    collateral: nullish(z.string()),
    product: nullish(z.string()),
    user: nullish(z.string()),
    userInfo: nullish(
        z.object({
            name: nullish(z.string()),
            email: nullish(z.string().toLowerCase().email()),
        }),
    ),
    company: z.string(),
    event: nullish(z.string()),
    contact: nullish(z.array(z.string())),
    note: nullish(z.string()),
    // Scans saved before the source was tracked have none; they count as booth scans.
    source: z
        .nativeEnum(QrScanSource)
        .nullish()
        .transform((value) => value ?? QrScanSource.QR_CODE),
    createdAt: nullish(z.string()), // do not make non optional, use QrScanPayloadSchema instead
    deletedAt: nullish(z.string()),
    leadAccess: nullish(LeadAccessSchema),
    lockedPreview: nullish(LockedPreviewSchema),
});

export const QrScanPayloadSchema = QrScanSchema.extend({
    id: z.string(),
    createdAt: z.string(),
});

export type QrScan = z.infer<typeof QrScanSchema>;
export type QrScanPayload = z.infer<typeof QrScanPayloadSchema>;

export enum QrScanSummaryType {
    SUMMARIZE = 'summarize',
    VISITOR_NOTES = 'visitorNotes',
}

export const QrScanAISummarySchema = z.object({
    type: z.nativeEnum(QrScanSummaryType),
    summary: z.string(),
    user: z.string().optional(),
    email: z.string().optional(),
    company: z.string().optional(),
    event: z.string().optional(),
    dataHash: z.string().optional(),
    updatedAt: z.string().optional(),
    createdAt: z.string().optional(),
    createdBy: z.string().optional(),
    team: z.string().optional(),
    cached: z.boolean().optional(),
});

export type QrScanAISummary = z.infer<typeof QrScanAISummarySchema>;
